<script lang="ts">
	import * as Glide from "@glidejs/glide";
	import type { ActionReturn } from "svelte/action";
	import { disableGlidePerSlideAmount } from "../utils/disableGlidePerSlideAmount";
	import StaticPicture from "./Picture/StaticPicture.svelte";
	import Icon from "./Icon.svelte";
	import glideArrow from "../../../core/assets/icons/glide-arrow.svg?raw";
	import glideBack from "../../../core/assets/icons/glide-back.svg?raw";
	import { getTranslate } from "../utils/getTranslate";
	import translations from "./StoriesOfEmployees.translations.json";
	import { textLengthLimit } from "../../../core/utils/textLengthLimit";
	import vector from "../assets/images/Vector.png?webp";
	import { getTenant } from "../contexts/tenantContextKey";
	import type { LocalizedString } from "../../../core/schema/Locale";
	import type { Media } from "../../../core/schema/Media";
	import { nbspify } from "../../../core/utils/nbspify.js";

	interface PeopleSlider {
		name: LocalizedString;
		perex: LocalizedString;
		description: LocalizedString;
		thumbnailImage?: Media;
	}

	export let people: PeopleSlider[];
	export let customBg = "";

	$: hasThumbnailImage = people.some((person) => person.thumbnailImage?.path);

	const buttons: string[] = [glideBack, glideArrow];
	let currentIndex = 0;

	const { locale } = getTenant();

	const translate = getTranslate(translations);

	function glide(element: HTMLDivElement): ActionReturn {
		const glideOptions = {
			type: "slider",
			startAt: 0,
			focusAt: "center",
			perView: 1,
			hoverpause: false,
			animationDuration: 650,
			animationTimingFunc: "ease-in-out",
			gap: 9,
			keyboard: true,
			rewind: false,
			cloningRatio: 2,
		} satisfies Partial<Glide.Options>;

		// eslint-disable-next-line new-cap
		const glide = new Glide.default(element, { ...glideOptions }).mount();
		glide.on("resize", () => {
			disableGlidePerSlideAmount(glide, element);
		});

		glide.on(["mount.before", "run"], () => {
			currentIndex = glide.index;
		});

		return {
			destroy(): void {
				glide.destroy();
			},
		};
	}
	/* eslint-disable svelte/no-at-html-tags, @typescript-eslint/no-unsafe-member-access */
</script>

<div class={customBg}>
	<div use:glide class="glide relative w-full pb-12 min-[500px]:pb-0">
		<div class="container pt-20 text-xl font-semibold leading-[1.13] lg:hidden">{@html translate("headline")}</div>
		<div class="glide__track relative overflow-hidden" data-glide-el="track">
			<div class="glide__slides inset-0 z-30 flex lg:absolute">
				{#each people as person}
					<div class="glide__slide flex justify-center bg-transparent py-4 lg:h-[61.375rem] lg:py-[3.75rem]">
						<div
							class="flex w-full max-w-[120rem] flex-col-reverse px-5 lg:flex-row-reverse lg:pr-[2rem] xl:pr-[4.375rem]"
						>
							<div
								class="relative z-10 hidden h-[25.25rem] w-full flex-col justify-between pb-[1.875rem] pt-[2.063rem] lg:flex lg:h-auto"
							>
								<div
									class="z-10 flex h-full flex-col justify-between self-end bg-white lg:w-[42rem] lg:px-[3.625rem] lg:pb-16 lg:pt-[4.625rem] xl:w-[49.125rem]"
								>
									<p class="text-2lg text-wrap font-bold leading-[1.31]">{nbspify(person.name[locale])}</p>
									<p class="lg:text-2lg md:text-2md line-clamp-[16] text-wrap text-sm">
										{nbspify(textLengthLimit(person.perex[locale]))}
									</p>
									<p class="md:text-2md max-h-[26.25rem] overflow-auto text-wrap text-xs">
										{nbspify(textLengthLimit(person.description[locale]))}
									</p>
								</div>
								{#if person.thumbnailImage}
									<StaticPicture
										image={person.thumbnailImage.path}
										alt={person.name[locale]}
										width={1118}
										height={1118}
										imgClass="max-h-[40rem] lg:max-h-[69.875rem] max-w-[40.25rem] lg:max-w-[69.875rem] mt-20 lg:mt-[18rem] lg:-ml-[9rem] object-contain {!hasThumbnailImage &&
											'lg:max-h-[49.875rem] lg:max-w-[49.875rem]'}"
										class="absolute top-auto -z-20 flex h-full w-full items-center lg:left-[16rem] xl:left-[22rem] min-[1700px]:-top-[3rem]"
									/>
								{/if}
							</div>
							<div
								class="z-10 -mt-10 flex h-auto w-full flex-col justify-between bg-white px-7 pb-[1.875rem] pt-[2.063rem] lg:hidden"
							>
								<p class="text-lg font-semibold min-[425px]:text-xl md:text-2xl lg:text-4xl">
									{nbspify(person.name[locale])}
								</p>
								<p class="lg:text-2lg md:text-2md line-clamp-[19] text-sm">
									{nbspify(textLengthLimit(person.perex[locale]))}
								</p>
								<p class="md:text-2md max-h-[26.25rem] overflow-auto text-xs">
									{nbspify(textLengthLimit(person.description[locale]))}
								</p>
							</div>
							<div class="relative flex flex-col sm:gap-5 lg:hidden">
								<div class="mt-12 flex h-[19.375rem] w-[120%] sm:h-[25rem] md:h-[35rem]"></div>
								{#if person.thumbnailImage}
									<StaticPicture
										image={person.thumbnailImage.path}
										alt={person.name[locale]}
										width={1118}
										height={1118}
										imgClass="h-full md:max-h-[40rem] object-contain"
										class="absolute inset-0 flex h-full w-full translate-y-[16%] items-center justify-center lg:hidden"
									/>
								{/if}
							</div>
						</div>
					</div>
				{/each}
			</div>
			<div
				class="container inset-0 z-20 mb-[7rem] ml-[3rem] mr-0 hidden w-1/2 justify-center gap-[7rem] pt-[4.19rem] lg:absolute lg:mb-0 lg:inline-block lg:flex-col lg:justify-normal lg:pt-[9rem]"
			>
				<h2 class="z-0 ml-0 hidden font-semibold leading-[1.13] lg:inline-block lg:text-[4.625rem]">
					{@html translate("headline")}
				</h2>
			</div>
			<div
				class="container inset-0 z-30 mb-[7rem] ml-[4rem] mr-0 flex w-1/2 justify-center gap-[7rem] pt-[4.19rem] lg:absolute lg:mb-0 lg:mt-48 lg:flex-col lg:justify-normal lg:pt-[9rem]"
			>
				<div class="top-0 z-30 flex w-full justify-center gap-16 lg:justify-normal" data-glide-el="controls">
					{#each buttons as button, index}
						<button
							aria-label={`Posuňte ${index === 0 ? "doleva" : "doprava"}`}
							data-glide-dir={index === 0 ? "<" : ">"}
							class="focus:outline-none"
						>
							<Icon
								icon={button}
								class="h-[3.5625rem] text-black lg:h-auto {(index - 1 && currentIndex === 0) ||
								(currentIndex === people.length - 1 && index === 1)
									? 'opacity-20'
									: ''}"
							/>
						</button>
					{/each}
				</div>
			</div>
			<div
				class="z-10 hidden h-[20rem] w-full min-[360px]:h-[25rem] min-[400px]:h-[30rem] md:h-[35rem] lg:mb-64 lg:flex lg:h-[69.875rem] {!hasThumbnailImage &&
					'lg:max-h-[49.875rem] lg:max-w-[49.875rem]'}"
			>
				<StaticPicture
					image={vector}
					alt="Vector"
					width={1920}
					height={1080}
					imgClass="w-full h-full object-contain"
					class="index-0 absolute -z-10 hidden h-full w-full items-center justify-center lg:flex"
				/>
			</div>
		</div>
	</div>
</div>
